import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowRight,
  ArrowLeft,
  Book,
  Home,
  Newspaper,
  Loader2,
} from "lucide-react";
import api from "@/lib/api";

const STR = {
  fr: {
    home: "Accueil",
    dictionary: "Dictionnaire",
    blog: "Blog",
    start: "Commencer",
    loading: "Chargement…",
    notFound: "Cet article n'existe pas ou plus.",
    back: "Retour au blog",
    ctaTitle: "Apprends l'arabe pour de bon",
    ctaBody:
      "Un programme quotidien, une vraie progression, un dictionnaire complet.",
    ctaButton: "Essayer gratuitement",
  },
  en: {
    home: "Home",
    dictionary: "Dictionary",
    blog: "Blog",
    start: "Get started",
    loading: "Loading…",
    notFound: "This article doesn't exist, or was removed.",
    back: "Back to the blog",
    ctaTitle: "Learn Arabic for real",
    ctaBody: "A daily program, real progress, a complete dictionary.",
    ctaButton: "Try for free",
  },
};

function PublicNav({ lang, t }) {
  const nav = useNavigate();
  const homeHref = lang === "en" ? "/" : "/fr";
  const authHref = lang === "en" ? "/auth" : "/fr/auth";
  const dictHref = lang === "en" ? "/dictionary" : "/fr/dictionary";
  const blogHref = lang === "en" ? "/blog" : "/fr/blog";
  return (
    <header className="sticky top-0 z-40 pt-4 pb-3 bg-[#070d14]/90 backdrop-blur-xl border-b border-white/[0.06]">
      <div className="max-w-3xl mx-auto px-4 flex items-center gap-2">
        <Link to={homeHref} className="flex items-center gap-2.5 shrink-0 mr-2">
          <div className="w-9 h-9 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center font-head font-bold arabic">
            ع
          </div>
          <span className="hidden sm:block font-head font-extrabold text-lg">
            Harf
          </span>
        </Link>
        <nav className="flex items-center gap-1 rounded-full border border-white/12 bg-white/[0.05] px-1.5 py-1.5">
          <Link
            to={homeHref}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 transition"
          >
            <Home size={14} />{" "}
            <span className="hidden sm:inline">{t.home}</span>
          </Link>
          <Link
            to={dictHref}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 transition"
          >
            <Book size={14} /> {t.dictionary}
          </Link>
          <Link
            to={blogHref}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 transition"
          >
            <Newspaper size={14} /> {t.blog}
          </Link>
        </nav>
        <div className="flex-1" />
        <button
          onClick={() => nav(authHref)}
          className="px-5 py-2.5 rounded-full bg-white/12 border border-white/20 backdrop-blur-xl text-sm font-semibold hover:bg-white/20 transition shrink-0"
        >
          {t.start}
        </button>
      </div>
    </header>
  );
}

export default function BlogPost({ lang = "fr" }) {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const t = STR[lang] || STR.fr;

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    api
      .get(`/blog/posts/${slug}?lang=${lang}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true));
  }, [slug, lang]);

  useEffect(() => {
    if (!post) return;
    document.title = `${post.title} | Harf`;
    const setMeta = (attr, key, content) => {
      let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, key);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };
    setMeta("name", "description", post.seo_description || post.excerpt || "");
    setMeta("property", "og:title", post.title);
    setMeta(
      "property",
      "og:description",
      post.seo_description || post.excerpt || "",
    );
    setMeta("property", "og:type", "article");

    const path = lang === "en" ? `/blog/${post.slug}` : `/fr/blog/${post.slug}`;
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", window.location.origin + path);

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "harf-blog-jsonld";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.seo_description || post.excerpt || "",
      datePublished: post.published_at,
      inLanguage: post.lang,
      publisher: { "@type": "Organization", name: "Harf" },
    });
    document.head.appendChild(ld);

    return () => {
      const old = document.getElementById("harf-blog-jsonld");
      if (old) old.remove();
    };
  }, [post, lang]);

  const backHref = lang === "en" ? "/blog" : "/fr/blog";

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#070d14] text-white">
        {!user && <PublicNav lang={lang} t={t} />}
        <div className="flex flex-col items-center justify-center px-4 text-center py-24">
          <p className="text-white/60 mb-6">{t.notFound}</p>
          <Link
            to={backHref}
            className="px-6 py-3 rounded-full bg-white text-[#070d14] font-semibold"
          >
            {t.back}
          </Link>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-[#070d14] text-white">
        {!user && <PublicNav lang={lang} t={t} />}
        <div className="flex items-center justify-center gap-2 text-white/40 text-sm py-24">
          <Loader2 size={16} className="animate-spin" /> {t.loading}
        </div>
      </div>
    );
  }

  // Rendu simple : chaque paragraphe (séparé par une ligne vide côté
  // admin) devient un <p>. Pas de moteur markdown : suffisant pour un
  // article de blog, sans dépendance supplémentaire.
  const paragraphs = post.content.split(/\n\s*\n/).filter((p) => p.trim());

  return (
    <div className="min-h-screen bg-[#070d14] text-white">
      {!user && <PublicNav lang={lang} t={t} />}

      <main className="max-w-2xl mx-auto px-4 py-12">
        <Link
          to={backHref}
          className="inline-flex items-center gap-2 text-sm text-white/45 hover:text-white/80 transition mb-8"
        >
          <ArrowLeft size={15} /> {t.blog}
        </Link>

        <article>
          <h1 className="font-head text-3xl sm:text-4xl font-bold mb-6 leading-tight">
            {post.title}
          </h1>
          {post.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8">
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-[11px] font-bold uppercase tracking-wider text-white/40 rounded-full border border-white/12 px-3 py-1"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
          <div className="space-y-4">
            {paragraphs.map((p, i) => (
              <p key={i} className="text-[16px] text-white/70 leading-relaxed">
                {p}
              </p>
            ))}
          </div>
        </article>

        <div className="mt-16 rounded-[2rem] border border-white/[0.1] bg-white/[0.02] p-8 sm:p-10 text-center">
          <h2 className="font-head text-xl sm:text-2xl font-bold mb-3">
            {t.ctaTitle}
          </h2>
          <p className="text-white/55 max-w-lg mx-auto mb-7">{t.ctaBody}</p>
          <button
            onClick={() => nav(authHref)}
            className="px-7 py-3.5 rounded-full bg-white text-[#070d14] font-bold hover:bg-white/90 transition inline-flex items-center gap-2"
          >
            {t.ctaButton} <ArrowRight size={17} />
          </button>
        </div>
      </main>
    </div>
  );
}
